import { api } from "@/lib/api";
import type { Env, StatisticsSnapshot } from "@/lib/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const POLL_60S = { refetchInterval: 60_000 };

// IR-1046 (Rotem 2026-06-09): the Analytics tab scopes to the selected env.
// Backend reads ?env=<dev|prod> via _env_db and routes to the matching DB
// alias (default prod). Forward env on every call so dev/prod don't mix.
function envQuery(env: Env): string {
  return `?env=${env}`;
}

export function useStatistics(env: Env = "prod") {
  return useQuery({
    queryKey: ["statistics", env],
    queryFn: () => api<StatisticsSnapshot>(`/api/ops/v1/data/statistics${envQuery(env)}`),
    ...POLL_60S,
  });
}

export function useRefreshStatistics(env: Env = "prod") {
  const qc = useQueryClient();
  return useMutation({
    // Refresh is prod-only — the backend recomputes against its local (prod)
    // DB and rejects ?env=dev (400). The Analytics page disables the button
    // on the dev toggle; env is still forwarded for the server-side backstop.
    mutationFn: () =>
      api<StatisticsSnapshot>(`/api/ops/v1/data/statistics/refresh${envQuery(env)}`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["statistics", env] }),
  });
}
